import { forwardRef, createContext, useState, useLayoutEffect } from "react";
import styled, { css, useTheme } from "styled-components";
import Box from "../box";
import Button from "../button";
import { useControllableState, useMediaQuery } from "../../hooks";
import { LeftOutlined, RightOutlined } from "../../icons";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const SiderContext = /*#__PURE__*/createContext({});
const CollapseButton = styled(Button).withConfig({
  displayName: "sider__CollapseButton",
  componentId: "sc-e9eyr5-0"
})(["&.ant-btn{position:absolute;top:52px;box-shadow:", ";opacity:", ";z-index:2;", " ", " &:focus{opacity:1;}}"], p => p.theme.shadows.base, p => p.opacity, p => p.align === "left" && css(["left:100%;transform:translate(-50%);"]), p => p.align === "right" && css(["right:100%;transform:translate(50%);"]));
const Sider = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    align,
    width,
    collapsedWidth,
    collapsible,
    collapsed,
    defaultCollapsed = false,
    breakpoint = "md",
    onCollapseToggle,
    onCollapse,
    onBreakpoint,
    bg,
    color,
    children,
    ...rest
  } = _ref;
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);
  const [isCollapsed, setCollapsed] = useControllableState(collapsed, onCollapseToggle, defaultCollapsed);
  const toggleCollapsed = () => setCollapsed(!isCollapsed);
  const breakpointActive = useMediaQuery(`screen and (max-width: ${theme.breakpoints[breakpoint]})`);
  const siderCollapsed = isCollapsed || breakpointActive;
  const currentWidth = siderCollapsed ? collapsedWidth : width;
  const triggerVisible = hovered || siderCollapsed;
  const icons = {
    expand: align === "left" ? /*#__PURE__*/_jsx(RightOutlined, {}) : /*#__PURE__*/_jsx(LeftOutlined, {}),
    collapse: align === "left" ? /*#__PURE__*/_jsx(LeftOutlined, {}) : /*#__PURE__*/_jsx(RightOutlined, {})
  };
  const currentIcon = icons[siderCollapsed ? "expand" : "collapse"];
  useLayoutEffect(() => {
    onCollapse?.(siderCollapsed);
  }, [siderCollapsed]);
  useLayoutEffect(() => {
    onBreakpoint?.(breakpointActive);
  }, [breakpointActive]);
  return /*#__PURE__*/_jsx(SiderContext.Provider, {
    value: {
      siderCollapsed,
      collapsedWidth
    },
    children: /*#__PURE__*/_jsxs(Box, {
      ref: ref,
      as: "aside",
      ...rest,
      position: "relative",
      width: currentWidth,
      bg: bg,
      color: color,
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
      style: {
        boxSizing: "content-box"
      },
      children: [collapsible && !breakpointActive && /*#__PURE__*/_jsx(CollapseButton, {
        align: align,
        opacity: triggerVisible ? 1 : 0,
        shape: "circle",
        size: "small",
        icon: currentIcon,
        onClick: toggleCollapsed
      }), children]
    })
  });
});
Sider.defaultProps = {
  align: "left",
  width: 200,
  collapsedWidth: 72,
  collapsible: false,
  bg: "bg.body"
};
export default Sider;